import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { ModalGeneralInfoTable } from 'design-system/src/components/GeneralInfoTable/ModalGeneralInfoTable';
import { client, type Drug, type Patient, type Prescriber } from '../../../api';

type CompletePrescriptionModalProps = {
  closeModal: () => void;
  rxId: string;
  patient: Patient;
  drug: Drug;
  prescriber: Prescriber | null;
  prescribedOn: string | null;
};

const CompletePrescriptionModal = ({
  closeModal,
  rxId,
  patient,
  drug,
  prescriber,
  prescribedOn,
}: CompletePrescriptionModalProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "complete prescription" for drug ID: ${drug.id}`);
        return;
      }
      const res = await client.post(`rxs/${drug.id}/complete`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rxs', rxId] });
      toast.custom(() => <Toast variant="success">Prescription has been moved to Completed</Toast>);
      // navigate back to the previous page instead of prescriptions page to restore url query params
      navigate(-1);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  return (
    <Modal onClose={closeModal}>
      <ModalTitle>Move prescription to Completed</ModalTitle>

      <ModalBody>
        <ModalGeneralInfoTable
          patientName={patient ? `${patient.first_name} ${patient.last_name}` : null}
          prescriberName={prescriber?.name || null}
          dob={patient?.date_of_birth || null}
          prescribedOn={prescribedOn || null}
          drugName={drug?.name || null}
          prescriptionId={drug?.id || null}
        />

        <Typography styledAs="bodyDMSans">
          Please confirm that this prescription has already been completed and that the patient has received it. Once
          confirmed, the prescription will be moved to Completed.
        </Typography>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={closeModal} size="sm">
          Back
        </Button>
        <Button
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isPending}
          isLoading={submitMutation.isPending}
          size="sm"
        >
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CompletePrescriptionModal;
